import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Suspense } from 'react'
import { PageContextProvider, usePageContext } from '@/lib/context/page'
import { PeriodType } from '@/types'
import { useGetDeatiledSingleFinancialModelQuery } from '@/feature/financial-model/api'
import { PageBody, PageHeader } from '@/components/shared/page-sections.tsx'
import { EcommerceCommonHeader } from '@/components/shared/headers.tsx'
import { CogsViewSwitcher } from '@/feature/financial-model/ecommerce/cogs/components/CogsViewSwitcher.tsx'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import { Container, ScrollableRouteContainer } from '@/components/shared/Container.tsx'
import { NavigationProvider } from '@/lib/context/navigation'
import { useGetDirectLabourDataSuspenseQuery } from '@/feature/financial-model/ecommerce/cogs/api'
import { ForecastDirectLabourTable, HistoricalDirectLabourTable } from '@/feature/financial-model/ecommerce/cogs/components/direct-labour-tables.tsx'

export const CogsDirectLabourPeriodRoute = () => {
  const { period } = useParams<{ period: PeriodType }>()
  if (!period) {
    throw new Error(`CogsDirectLabourPeriodRoute > period is undefined`)
  }
  return (
    <PageContextProvider
      defaultState={{
        period,
        view: `monthly`,
        queryPeriod: period
      }}
    >
      <CogsDirectLabourPeriod />
    </PageContextProvider>
  )
}

export const CogsDirectLabourPeriod = () => {
  const { id: financialModelId, period } = useParams<{
    id: string
    period: PeriodType
  }>()

  if (!financialModelId) {
    throw new Error(`CogsDirectLabourPeriod > financialModelId is undefined`)
  }

  const { t } = useTranslation(`cogs`)
  const { state } = usePageContext()

  const { financialModel } = useGetDeatiledSingleFinancialModelQuery(financialModelId)

  const { data: departmentData } = useGetDirectLabourDataSuspenseQuery(financialModelId, state.queryPeriod, state.view)

  return (
    <ScrollableRouteContainer className={`p-0`}>
      <PageHeader>
        <EcommerceCommonHeader
          linkText={t(`cogs.home.header.back`)}
          path={`/dashboard/financial-model/${financialModelId}/ecommerce/cogs/${period}-data`}
        />
        <div className='mt-6 flex justify-between'>
          <div>
            <div className='flex items-center'>
              <h1 className='page-title'>
                {t(`cogs.periods.direct_labour.title`)}
                {` - `}
                <span className='page-title-secondary-content capitalize'>{period}</span>
              </h1>
            </div>
            <p className='page-subtitle'>{`${t(`cogs.home.header.subtitle`)} | ${financialModel?.data?.name}`}</p>
          </div>
          <CogsViewSwitcher />
        </div>
      </PageHeader>
      <PageBody>
        <Container>
          <Suspense fallback={<Skeleton className='h-[500px] w-full' />}>
            <NavigationProvider>
              {state.period === `historical` ? (
                <HistoricalDirectLabourTable periods={departmentData.periods} />
              ) : (
                <ForecastDirectLabourTable config={departmentData.config} periods={departmentData.periods} />
              )}
            </NavigationProvider>
          </Suspense>
        </Container>
      </PageBody>
    </ScrollableRouteContainer>
  )
}
